import { useState, useEffect, useCallback, memo, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
// framer-motion removed — pill uses CSS transitions for jitter-free sliding
import {
  Trophy,
  UsersThree,
  ListChecks,
  Timer,
  GearSix,
} from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { hapticLight } from '@/lib/native/haptics'
import { track } from '@/lib/analytics'
import { isNativePlatform, isIOS } from '@/lib/native'

const IOS_HOME_INDICATOR_HEIGHT = 34
const ICON_SIZE = 24

interface NavItem {
  label: string
  href: string
  Icon: Icon
}

const navItems: NavItem[] = [
  { label: 'Quests', href: '/challenges', Icon: Trophy },
  { label: 'Social', href: '/community', Icon: UsersThree },
  { label: 'Habits', href: '/', Icon: ListChecks },
  { label: 'Focus', href: '/pomodoro', Icon: Timer },
  { label: 'Settings', href: '/settings', Icon: GearSix },
]

const ITEM_COUNT = navItems.length
const PILL_WIDTH_PERCENT = 100 / ITEM_COUNT

export const BottomNav = memo(function BottomNav() {
  const location = useLocation()
  const [safeAreaBottom, setSafeAreaBottom] = useState<number>(0)

  const getSafeAreaBottom = useCallback(() => {
    const nativeValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--native-safe-area-inset-bottom')
      .trim()

    if (nativeValue && nativeValue !== '0px' && nativeValue !== '') {
      const parsed = parseFloat(nativeValue)
      if (!isNaN(parsed) && parsed > 0) {
        return parsed
      }
    }

    if (
      typeof window !== 'undefined' &&
      (window as unknown as { nativeSafeAreaInsets?: { bottom: number } })
        .nativeSafeAreaInsets
    ) {
      const bottom = (
        window as unknown as { nativeSafeAreaInsets: { bottom: number } }
      ).nativeSafeAreaInsets.bottom
      if (bottom > 0) {
        return bottom
      }
    }

    const testDiv = document.createElement('div')
    testDiv.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)'
    document.body.appendChild(testDiv)
    const computed = getComputedStyle(testDiv).paddingBottom
    document.body.removeChild(testDiv)

    const envValue = parseFloat(computed)
    if (!isNaN(envValue) && envValue > 0) {
      return envValue
    }

    if (isNativePlatform() && isIOS) {
      return IOS_HOME_INDICATOR_HEIGHT
    }

    return 0
  }, [])

  useEffect(() => {
    const updateSafeArea = () => {
      const value = getSafeAreaBottom()
      setSafeAreaBottom(value)
    }

    updateSafeArea()
    const timers = [
      setTimeout(updateSafeArea, 100),
      setTimeout(updateSafeArea, 500),
      setTimeout(updateSafeArea, 1000),
      setTimeout(updateSafeArea, 2000),
    ]

    const handleNativeSafeAreaChange = (
      e: CustomEvent<{ bottom: number }>
    ) => {
      setSafeAreaBottom(e.detail.bottom)
    }

    window.addEventListener(
      'nativeSafeAreaInsetsChanged',
      handleNativeSafeAreaChange as EventListener
    )
    window.addEventListener('resize', updateSafeArea)

    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener(
        'nativeSafeAreaInsetsChanged',
        handleNativeSafeAreaChange as EventListener
      )
      window.removeEventListener('resize', updateSafeArea)
    }
  }, [getSafeAreaBottom])

  const activeIndex = useMemo(() => {
    const idx = navItems.findIndex((item) => {
      if (item.href === '/') return location.pathname === '/'
      return location.pathname.startsWith(item.href)
    })
    return idx === -1 ? 2 : idx
  }, [location.pathname])

  const safeAreaHeight =
    safeAreaBottom > 0
      ? `${safeAreaBottom}px`
      : 'env(safe-area-inset-bottom, 0px)'

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
      style={{ willChange: 'transform', transform: 'translateZ(0)' }}
    >
      <div
        className={cn(
          'bg-white/80 dark:bg-slate-950/80',
          'backdrop-blur-xl',
          'border-t border-zinc-200/60 dark:border-white/[0.06]',
        )}
      >
        <div className="relative flex items-center h-[50px] mx-1">
          {/* Sliding pill indicator — CSS transition for jitter-free animation */}
          <div
            className="absolute top-1 bottom-1 left-0 rounded-xl bg-black/[0.05] dark:bg-white/[0.08] transition-transform duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]"
            style={{
              width: `${PILL_WIDTH_PERCENT}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />

          {navItems.map((item, i) => {
            const active = i === activeIndex

            return (
              <Link
                key={item.href}
                to={item.href}
                aria-label={item.label}
                onClick={() => {
                  hapticLight()
                  track(
                    'bottom_nav_click',
                    { target: item.href },
                    'navigation'
                  )
                }}
                className={cn(
                  'relative z-10 flex flex-1 items-center justify-center h-full',
                  'outline-none active:scale-90 transition-transform duration-100',
                  active
                    ? 'text-zinc-900 dark:text-zinc-100'
                    : 'text-zinc-400 dark:text-zinc-500',
                )}
              >
                <item.Icon
                  size={ICON_SIZE}
                  weight={active ? 'fill' : 'regular'}
                />
              </Link>
            )
          })}
        </div>

        {/* Safe area spacer for home indicator */}
        <div style={{ height: safeAreaHeight }} />
      </div>
    </nav>
  )
})
